import {getApiUrl, getBackend} from '../utils/processHelpers';

/**
 * The base url path for opening app url as a config.
 */
export const MANIFEST_BASE_URL = 'api/app.json?appurl=';

export enum ManifestType {
    APP_URL = 'appUrl',
    MANIFEST = 'manifest',
    PATH = 'path',
}

export interface ManifestImages {
    shortcut?: {
        icon: string;
        name: string;
    };
    splashScreenImage?: string;
    startup_app?: {
        icon: string;
    };
}

export interface Manifest extends ManifestImages {
    [key: string]: any;
    startup_app?: {
        icon: string;
        name?: string;
        url?: string;
    };
    platform?: {
        uuid?: string;
    };
}

/**
 * Get the application URI for a manifest type.
 *
 * Converts a manifest url to the appropriate format. `Path` & `Manifest` are unchanged.
 *
 * E.g. `http://google.com` -> `http://os.openfin.co/api/app.json?appUrl=http://google.com`
 */
export function getApplicationURI(manifestType: ManifestType, uri: string): string {
    if (manifestType === ManifestType.APP_URL) {
        return createManifestFromAppUri(uri);
    }
    return uri;
}

/**
 * Create an full manifest config url from a given app URL.
 *
 * E.g. `http://google.com` -> `http://os.openfin.co/api/app.json?appUrl=http://google.com`
 */
function createManifestFromAppUri(appUrl: string, baseUrl?: string, runtimeVersion?: string) {
    const apiBackend = getApiUrl().includes('http://localhost') ? `${getBackend()}` : getApiUrl();
    const url = `${baseUrl || apiBackend}${MANIFEST_BASE_URL}${appUrl}`;

    return runtimeVersion ? `${url}&runtime=${runtimeVersion}` : url;
}

/**
 * Get a user readable string for a manifest type.
 */
export function getManifestTypeDisplayName(manifestType: ManifestType): string {
    switch (manifestType) {
        case ManifestType.APP_URL:
            return 'App URL';
        case ManifestType.MANIFEST:
            return 'Manifest URL';
        case ManifestType.PATH:
            return 'Path';
        default:
            return 'Unknown manifest type';
    }
}

/**
 * Get the display string for a manifest URI.
 * If `ManifestType.APP_URL` this will strip the `MANIFEST_BASE_URL`.
 *
 * E.g. `http://os.openfin.co/api/app.json?appurl=http://google.com` will be transformed to
 * `http://google.com` which is the user facing URI;
 */
export function getManifestDisplayUri(manifestType: ManifestType, uri: string): string {
    if (manifestType !== ManifestType.APP_URL) {
        return uri;
    }
    const foundIndex = uri.indexOf(MANIFEST_BASE_URL);
    const sliceStart = foundIndex === -1 ? 0 : foundIndex + MANIFEST_BASE_URL.length;
    return uri.slice(sliceStart);
}
